/**
 * Build Aquifer Summary: Aquifer -> Year -> Agg System hierarchy
 *
 * Production values are summed from filtered wells.
 * Permits use system-level values (once per system+year).
 */

import { computePctPermit, compareAggSystems } from "../utils";
import { createEmptyTotals, accumulateWellDetailRow } from "./buildTotals";

/**
 * Build aquifer summary data structure
 * @param {import("../types").ProductionRow[]} filteredRows - Filtered production rows
 * @returns {import("../types").AquiferSummary} Aquifer summary with grand totals
 */
export const buildAquiferSummary = (filteredRows) => {
  const aquiferMap = new Map();
  const grandTotals = createEmptyTotals();
  const grandSeenSystems = new Set();

  for (const row of filteredRows) {
    const aquiferKey = row.aquifer_ndx;
    const aquiferName = row.aquifer_name || "Unknown";
    const year = row.report_year;
    const aggKey = row.agg_system_ndx;
    const aggName = row.agg_system_name || "Unknown";

    if (!aquiferMap.has(aquiferKey)) {
      aquiferMap.set(aquiferKey, {
        aquifer_ndx: aquiferKey,
        aquifer_name: aquiferName,
        totals: createEmptyTotals(),
        seenSystems: new Set(),
        years: new Map(),
      });
    }

    const aquiferGroup = aquiferMap.get(aquiferKey);

    if (!aquiferGroup.years.has(year)) {
      aquiferGroup.years.set(year, {
        year,
        totals: createEmptyTotals(),
        seenSystems: new Set(),
        aggSystems: new Map(),
      });
    }

    const yearGroup = aquiferGroup.years.get(year);

    // For agg system totals
    if (!yearGroup.aggSystems.has(aggKey)) {
      yearGroup.aggSystems.set(aggKey, {
        agg_system_ndx: aggKey,
        agg_system_name: aggName,
        totals: createEmptyTotals(),
        seenSystems: new Set(),
      });
    }

    const aggSystemGroup = yearGroup.aggSystems.get(aggKey);

    // Accumulate filtered wells' production, permits once per system
    // This ensures consistency with DetailTable and ExcelTable
    accumulateWellDetailRow(
      aggSystemGroup.totals,
      row,
      aggSystemGroup.seenSystems
    );
    accumulateWellDetailRow(yearGroup.totals, row, yearGroup.seenSystems);
    accumulateWellDetailRow(aquiferGroup.totals, row, aquiferGroup.seenSystems);
    accumulateWellDetailRow(grandTotals, row, grandSeenSystems);
  }

  const aquifers = Array.from(aquiferMap.values())
    .map((aq) => ({
      aquifer_ndx: aq.aquifer_ndx,
      aquifer_name: aq.aquifer_name,
      totals: aq.totals,
      pct_permit: computePctPermit(aq.totals),
      years: Array.from(aq.years.values())
        .map((y) => ({
          year: y.year,
          totals: y.totals,
          pct_permit: computePctPermit(y.totals),
          aggSystems: Array.from(y.aggSystems.values())
            .map((a) => ({
              ...a,
              pct_permit: computePctPermit(a.totals),
            }))
            .sort((a, b) =>
              compareAggSystems(a.agg_system_name, b.agg_system_name)
            ),
        }))
        .sort((a, b) => b.year - a.year),
    }))
    .sort((a, b) => (a.aquifer_name || "").localeCompare(b.aquifer_name || ""));

  return {
    aquifers,
    grandTotals: {
      ...grandTotals,
      pct_permit: computePctPermit(grandTotals),
    },
  };
};
